import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute, sessionRequired, staffAuth } from "../../common";
import {
  AccountStanding,
  AppealForReview,
  AppealIdParams,
  AppealResponse,
  ClassificationIdParams,
  CreateAppealRequest,
  CreateFullSiteBanRequest,
  CreateReportRequest,
  DecideAppealRequest,
  DecideReportCaseRequest,
  FullSiteBanIdParams,
  FullSiteBanResponse,
  ListAppealReviewsQuery,
  ListAppealReviewsResponse,
  ListAppealsQuery,
  ListAppealsResponse,
  ListFullSiteBansQuery,
  ListFullSiteBansResponse,
  ListJudgedInputsQuery,
  ListJudgedInputsResponse,
  ListModeratedUsersQuery,
  ListModeratedUsersResponse,
  ListModerationSurfacesResponse,
  ListRefusalsQuery,
  ListRefusalsResponse,
  ListReportCasesQuery,
  ListReportCasesResponse,
  ListUserJudgementsQuery,
  ListUserJudgementsResponse,
  ModerationClassification,
  ModerationRefusal,
  ModerationStats,
  ModerationStatsQuery,
  OpenAppeal,
  ReclassifyRequest,
  ReportCaseDetail,
  ReportCaseIdParams,
  StatsParams,
  SurfaceBan,
  UserModeration,
  UserParams,
} from "./schemas";

export const service = {
  group: "moderation",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Moderation API",
  description:
    "Users report items, appeal automod's refusals, and read their account standing. Moderators work the report and appeal queues.",
} as const satisfies ContractService;

/**
 * Declared by every route that runs automod on what it saves. The submission is not saved, unless
 * the route's description says it is kept so the refusal can be appealed.
 */
export const moderationRefused = {
  MODERATION_REFUSED: {
    status: 422,
    message: "Automod refused the submission; data says why and whether it can be appealed.",
    data: ModerationRefusal,
  },
} as const;

/**
 * Declared by every route that runs automod on what it saves. Automod fails closed: when it can't
 * classify the submission, the submission is not saved.
 */
export const automodUnavailable = {
  AUTOMOD_UNAVAILABLE: {
    status: 503,
    message: "Automod couldn't check the submission, so it wasn't saved. Try again shortly.",
  },
} as const;

/** Declared by every write a surface ban can stop. */
export const surfaceBanned = {
  SURFACE_BANNED: {
    status: 403,
    message: "The account is banned from this surface; data says which and until when.",
    data: SurfaceBan,
  },
} as const;

/** Declared by every profile and collection write an open appeal can lock. */
export const appealOpen = {
  APPEAL_OPEN: {
    status: 409,
    message: "An open appeal locks this field; withdraw it to change the field.",
    data: OpenAppeal,
  },
} as const;

const account = { kind: "account" } as const;
const moderators = {
  kind: "account",
  note: "The caller also needs the moderation permission for the item's surface and queue, as the moderation model lists.",
} as const;

const noSuchAppeal = {
  NOT_FOUND: { message: "No appeal of yours has that id." },
} as const;

const noSuchCase = { NOT_FOUND: { message: "No report case has that id." } } as const;

const siteBanners = staffAuth("accounts.ban");

export const createReport = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/reports",
      operationId: "moderation.reports.create",
      summary: "Report an item.",
      description:
        "Adds the user's report to the item's open case, opening one if needed. The user is never told the outcome.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The report was received.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "The body is malformed, the reason cannot be given for this kind of item, or `other` has no details.",
    },
    ...sessionRequired,
    FORBIDDEN: { message: "A guest made the request, or the item is the user's own." },
    NOT_FOUND: { message: "The item does not exist, or the user cannot see it." },
    CONFLICT: { message: "The user has already reported this item, and its case is still open." },
  })
  .input(z.object({ body: CreateReportRequest }));

export const createAppeal = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/appeals",
      operationId: "moderation.appeals.create",
      summary: "Appeal a refusal.",
      description:
        "Sends one of the user's automod refusals to the heavy AI, or straight to a moderator if the AI already judged it, and locks what it refused until the appeal is decided or withdrawn. Until the heavy AI is built, every filing goes straight to a moderator.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new appeal.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: {
      message:
        "This refusal cannot be appealed, a moderator already decided an appeal of it, or it is more than six months old.",
    },
    NOT_FOUND: { message: "No refusal of the user's has that id." },
    CONFLICT: {
      message:
        "The user changed what was refused since, or an appeal is already open on it or on the same draft or field.",
    },
  })
  .input(z.object({ body: CreateAppealRequest }))
  .output(AppealResponse);

export const getAppeal = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/appeals/{appealId}",
      operationId: "moderation.appeals.get",
      summary: "Get an appeal.",
      description: "Returns one of the user's appeals.",
      inputStructure: "detailed",
      successDescription: "The appeal.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...noSuchAppeal })
  .input(z.object({ params: AppealIdParams }))
  .output(AppealResponse);

export const escalateAppeal = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/appeals/{appealId}/escalate",
      operationId: "moderation.appeals.escalate",
      summary: "Send an appeal to a moderator.",
      description:
        "Moves an appeal the heavy AI upheld to the human queue. A moderator's decision is final.",
      inputStructure: "detailed",
      successDescription: "The appeal, now waiting on a moderator.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...noSuchAppeal,
    CONFLICT: { message: "The appeal is not waiting for the user to escalate it." },
  })
  .input(z.object({ params: AppealIdParams }))
  .output(AppealResponse);

export const withdrawAppeal = oc
  .meta(
    paxRoute({ auth: account, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/appeals/{appealId}/withdraw",
      operationId: "moderation.appeals.withdraw",
      summary: "Withdraw an appeal.",
      description:
        "Ends an appeal waiting on the user or a moderator and unlocks what it was about. The refusal stands; the user can file again, straight to a moderator, while it is unchanged. Withdrawing a withdrawn appeal changes nothing.",
      inputStructure: "detailed",
      successDescription: "The withdrawn appeal.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...noSuchAppeal,
    CONFLICT: { message: "The heavy AI is still judging the appeal, or it has been decided." },
  })
  .input(z.object({ params: AppealIdParams }))
  .output(AppealResponse);

export const listAppeals = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/appeals",
      operationId: "moderation.appeals.list",
      summary: "List your appeals.",
      description:
        "Returns the user's appeals, newest first, optionally only open or closed ones, or only those about one item.",
      inputStructure: "detailed",
      successDescription: "A page of the user's appeals.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired })
  .input(z.object({ query: ListAppealsQuery }))
  .output(ListAppealsResponse);

export const listRefusals = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/refusals",
      operationId: "moderation.refusals.list",
      summary: "List refusals you can appeal.",
      description:
        "Returns the user's automod refusals that can still be appealed: appealable, unchanged since, at most six months old, and with no open or decided appeal. Newest first.",
      inputStructure: "detailed",
      successDescription: "The refusals the user can appeal.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired })
  .input(z.object({ query: ListRefusalsQuery }))
  .output(ListRefusalsResponse);

export const listAppealReviews = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/appeals/reviews",
      operationId: "moderation.appeals.reviews",
      summary: "List appeals for moderators.",
      description:
        "Returns the appeals waiting on a moderator, longest-waiting first, or the ones moderators decided, most recent first, each with the snapshot and classifications it is decided from. It holds only the kinds of item the caller moderates.",
      inputStructure: "detailed",
      successDescription: "A page of appeals.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate this surface or kind of item, or any." },
  })
  .input(z.object({ query: ListAppealReviewsQuery }))
  .output(ListAppealReviewsResponse);

export const decideAppeal = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/appeals/{appealId}/decide",
      operationId: "moderation.appeals.decide",
      summary: "Decide an appeal.",
      description:
        "Records the moderator's categorization of the refused snapshot. The policy then upholds or overturns the refusal; an overturned refusal voids its points and applies what was refused.",
      inputStructure: "detailed",
      successDescription: "The decided appeal.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate the appealed item's surface." },
    NOT_FOUND: { message: "No appeal has that id." },
    CONFLICT: { message: "The appeal is not waiting on a moderator." },
  })
  .input(z.object({ params: AppealIdParams, body: DecideAppealRequest }))
  .output(z.strictObject({ appeal: AppealForReview }));

export const getStanding = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/standing",
      operationId: "moderation.standing.get",
      summary: "Get your account standing.",
      description:
        "Returns the user's bans in force, the surfaces where their points are close to a ban, and their content taken down after reports in the last six months.",
      inputStructure: "detailed",
      successDescription: "The user's account standing.",
    }),
  )
  .errors({ ...sessionRequired })
  .output(AccountStanding);

export const listReportCases = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/cases",
      operationId: "moderation.cases.list",
      summary: "List report cases.",
      description:
        "Returns one human queue's open cases, oldest first, or decided cases, most recently decided first.",
      inputStructure: "detailed",
      successDescription: "A page of cases.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: {
      message: "The account can't work this queue, or this surface or kind of item in it.",
    },
  })
  .input(z.object({ query: ListReportCasesQuery }))
  .output(ListReportCasesResponse);

export const escalateReportCase = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/cases/{caseId}/escalate",
      operationId: "moderation.cases.escalate",
      summary: "Send a case to the lead moderators.",
      description:
        "Moves an open case from the moderator queue to the lead-moderator queue, for a case a moderator shouldn't decide alone.",
      inputStructure: "detailed",
      successDescription: "The case, now in the lead-moderator queue.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account can't work this kind of item in the moderator queue." },
    ...noSuchCase,
    CONFLICT: { message: "The case is closed, or not in the moderator queue." },
  })
  .input(z.object({ params: ReportCaseIdParams }))
  .output(ReportCaseDetail);

export const getReportCase = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/cases/{caseId}",
      operationId: "moderation.cases.get",
      summary: "Get a report case.",
      description:
        "Returns a case with its reports, every snapshot they refer to, and the classifications made for it so far.",
      inputStructure: "detailed",
      successDescription: "The case.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: {
      message: "The account can't work this kind of item in the queue the case is in.",
    },
    ...noSuchCase,
  })
  .input(z.object({ params: ReportCaseIdParams }))
  .output(ReportCaseDetail);

export const decideReportCase = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/cases/{caseId}/decide",
      operationId: "moderation.cases.decide",
      summary: "Decide a report case.",
      description:
        "Records the moderator's categorization of each input they judged, applies the consequences the policy gives, and closes the case.",
      inputStructure: "detailed",
      successDescription: "The closed case.",
    }),
  )
  .errors({
    BAD_REQUEST: {
      message:
        "The body is malformed, the snapshot is not one of the case's, or an input is not in the snapshot.",
    },
    ...sessionRequired,
    FORBIDDEN: {
      message: "The account can't work this kind of item in the queue the case is in.",
    },
    ...noSuchCase,
    CONFLICT: { message: "The case is closed, or still waiting on the heavy AI." },
  })
  .input(z.object({ params: ReportCaseIdParams, body: DecideReportCaseRequest }))
  .output(ReportCaseDetail);

export const getAppealReview = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/appeals/{appealId}/review",
      operationId: "moderation.appeals.review",
      summary: "Get an appeal to review.",
      description:
        "Returns one appeal with who filed it, the snapshot it is about, and every classification of that snapshot, whatever the appeal's status.",
      inputStructure: "detailed",
      successDescription: "The appeal.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate the appealed item's surface." },
    NOT_FOUND: { message: "No appeal has that id." },
  })
  .input(z.object({ params: AppealIdParams }))
  .output(z.strictObject({ appeal: AppealForReview }));

export const listJudgedInputs = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/classifications",
      operationId: "moderation.classifications.list",
      summary: "List judged inputs.",
      description:
        "Returns inputs on the surfaces the caller moderates, or on one, with every classification of each, most recently judged first, so automod's, the heavy AI's, and moderators' judgements can be compared.",
      inputStructure: "detailed",
      successDescription: "A page of judged inputs.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate this surface, or any." },
  })
  .input(z.object({ query: ListJudgedInputsQuery }))
  .output(ListJudgedInputsResponse);

export const reclassify = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/classifications/{classificationId}/reclassify",
      operationId: "moderation.classifications.reclassify",
      summary: "Reclassify a judgement.",
      description:
        "Records a lead moderator's categorization of what a classification judged, in its place. The old one's points are voided, which lifts any ban they no longer hold up; the new one's consequences are applied, which can ban; and content still shown is taken down if the new ruling acts. Content already refused or taken down stays so.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new classification.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account can't lead-moderate the judged item's surface." },
    NOT_FOUND: { message: "No classification has that id." },
    CONFLICT: {
      message:
        "A later classification of the same content already replaced it, or an appeal of it is open.",
    },
  })
  .input(z.object({ params: ClassificationIdParams, body: ReclassifyRequest }))
  .output(z.strictObject({ classification: ModerationClassification }));

export const listSurfaces = oc
  .meta(
    paxRoute({ auth: account, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/surfaces",
      operationId: "moderation.surfaces.list",
      summary: "List the surfaces you moderate.",
      description:
        "Returns each surface the caller holds a moderation permission on, what they can do there, and what waits on moderators. Empty for anyone else.",
      inputStructure: "detailed",
      successDescription: "The caller's surfaces.",
    }),
  )
  .errors({ ...sessionRequired })
  .output(ListModerationSurfacesResponse);

export const getSurfaceStats = oc
  .meta(
    paxRoute({
      auth: {
        kind: "account",
        note: "The caller also needs the surface's stats permission, such as `profile_moderation_stats.view`, or `live_game_moderation_stats.view` for live games.",
      },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/surfaces/{surface}/stats",
      operationId: "moderation.surfaces.stats",
      summary: "Get a surface's moderation stats.",
      description:
        "Counts how one surface's moderation went over a period: what automod flagged and acted on, how its labels held up category by category when moderators reviewed them, which categories are given together, reports and appeals and how long they took, bans including across surfaces, and each moderator's work.",
      inputStructure: "detailed",
      successDescription: "The surface's stats.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account can't view this surface's stats." },
  })
  .input(z.object({ params: StatsParams, query: ModerationStatsQuery }))
  .output(ModerationStats);

export const listUsers = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "search" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/users",
      operationId: "moderation.users.list",
      summary: "List and search moderated users.",
      description:
        "Finds users by exact id or the start of their username or display name, or lists those most recently given a consequence, each with their standing on every surface the caller moderates.",
      inputStructure: "detailed",
      successDescription: "The users found.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate any surface." },
  })
  .input(z.object({ query: ListModeratedUsersQuery }))
  .output(ListModeratedUsersResponse);

export const getUser = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/surfaces/{surface}/users/{userId}",
      operationId: "moderation.surfaces.users.get",
      summary: "Get a user's moderation record.",
      description:
        "Returns a user's points and bans on one surface, with the latest reports about what they answer for and their appeals there.",
      inputStructure: "detailed",
      successDescription: "The user's record.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate this surface." },
  })
  .input(z.object({ params: UserParams }))
  .output(UserModeration);

export const listUserJudgements = oc
  .meta(
    paxRoute({ auth: moderators, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/surfaces/{surface}/users/{userId}/judgements",
      operationId: "moderation.surfaces.users.judgements",
      summary: "List a user's judgements.",
      description:
        "Returns every classification of what the user answers for on one surface, most recent first, with what each judged and what the policy did.",
      inputStructure: "detailed",
      successDescription: "A page of judgements.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account doesn't moderate this surface." },
  })
  .input(z.object({ params: UserParams, query: ListUserJudgementsQuery }))
  .output(ListUserJudgementsResponse);

export const listFullSiteBans = oc
  .meta(
    paxRoute({ auth: siteBanners, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/moderation/v1alpha1/full-site-bans",
      operationId: "moderation.fullSiteBans.list",
      summary: "List an account's full-site bans.",
      description:
        "Returns every full-site ban the account has had, lifted ones included, newest first.",
      inputStructure: "detailed",
      successDescription: "The account's full-site bans.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ query: ListFullSiteBansQuery }))
  .output(ListFullSiteBansResponse);

export const createFullSiteBan = oc
  .meta(
    paxRoute({ auth: siteBanners, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/full-site-bans",
      operationId: "moderation.fullSiteBans.create",
      summary: "Ban an account from the whole site.",
      description:
        "Records a permanent full-site ban, signs the account out everywhere, and stops every later sign-in. The account's content stays as it is.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new ban.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    FORBIDDEN: { message: "The account can't ban accounts, or tried to ban itself." },
    NOT_FOUND: { message: "No account has that id." },
    CONFLICT: { message: "The account already has a full-site ban that hasn't been lifted." },
  })
  .input(z.object({ body: CreateFullSiteBanRequest }))
  .output(FullSiteBanResponse);

export const liftFullSiteBan = oc
  .meta(
    paxRoute({ auth: siteBanners, minimumTier: "normal", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/moderation/v1alpha1/full-site-bans/{banId}/lift",
      operationId: "moderation.fullSiteBans.lift",
      summary: "Lift a full-site ban.",
      description:
        "Records who lifted the ban and when, and unbans the account. The ban's row stays, so the account's history keeps it.",
      inputStructure: "detailed",
      successDescription: "The lifted ban.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No full-site ban has that id." },
    CONFLICT: { message: "The ban was already lifted." },
  })
  .input(z.object({ params: FullSiteBanIdParams }))
  .output(FullSiteBanResponse);
